/**
 * CRYIN — Theme Core JS
 * Vanilla JS, progressive enhancement, modular.
 * No jQuery, no framework dependencies.
 */

(function () {
  'use strict';

  /* -----------------------------------------------------------------------
     Dark Mode Toggle
     Moon/sun icon toggles between light and dark theme.
     ----------------------------------------------------------------------- */

  const themeToggle = document.querySelector('[data-theme-toggle]');

  if (themeToggle) {
    const THEME_KEY = 'cryin-theme';
    const saved = localStorage.getItem(THEME_KEY);

    // Apply saved preference or check system preference
    if (saved === 'dark' || (!saved && window.matchMedia('(prefers-color-scheme: dark)').matches)) {
      document.documentElement.setAttribute('data-theme', 'dark');
    }

    themeToggle.addEventListener('click', () => {
      const isDark = document.documentElement.getAttribute('data-theme') === 'dark';

      // Spin animation
      themeToggle.classList.add('is-spinning');
      themeToggle.addEventListener('animationend', () => {
        themeToggle.classList.remove('is-spinning');
      }, { once: true });

      if (isDark) {
        document.documentElement.removeAttribute('data-theme');
        localStorage.setItem(THEME_KEY, 'light');
      } else {
        document.documentElement.setAttribute('data-theme', 'dark');
        localStorage.setItem(THEME_KEY, 'dark');
      }
    });
  }

  /* -----------------------------------------------------------------------
     Grid Density Toggle
     "+" button cycles grid density: default (6-col) ↔ dense (3-col)
     ----------------------------------------------------------------------- */

  const gridToggle = document.querySelector('[data-grid-toggle]');

  if (gridToggle) {
    const STORAGE_KEY = 'cryin-grid-density';
    const saved = localStorage.getItem(STORAGE_KEY);

    if (saved === 'dense') {
      document.body.dataset.gridDensity = 'dense';
    }

    gridToggle.addEventListener('click', () => {
      const current = document.body.dataset.gridDensity || 'default';
      const next = current === 'default' ? 'dense' : 'default';

      const applyChange = () => {
        if (next === 'dense') {
          document.body.dataset.gridDensity = 'dense';
        } else {
          delete document.body.dataset.gridDensity;
        }
        localStorage.setItem(STORAGE_KEY, next);
      };

      // Use View Transitions for smooth grid reflow animation
      if (document.startViewTransition) {
        document.startViewTransition(applyChange);
      } else {
        applyChange();
      }
    });
  }

  /* -----------------------------------------------------------------------
     Mobile Nav (hamburger overlay)
     ----------------------------------------------------------------------- */

  const mobileNav = document.querySelector('[data-mobile-nav]');
  const mobileToggle = document.querySelector('[data-mobile-menu-toggle]');
  const mobileClose = document.querySelector('[data-mobile-menu-close]');

  function setMobileNav(open) {
    if (!mobileNav || !mobileToggle) return;
    mobileNav.setAttribute('aria-hidden', open ? 'false' : 'true');
    mobileNav.classList.toggle('is-open', open);
    mobileToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    document.body.classList.toggle('mobile-nav-open', open);
  }

  if (mobileToggle) {
    mobileToggle.addEventListener('click', () => {
      const isOpen = mobileNav && mobileNav.classList.contains('is-open');
      setMobileNav(!isOpen);
    });
  }
  if (mobileClose) {
    mobileClose.addEventListener('click', () => setMobileNav(false));
  }
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && mobileNav && mobileNav.classList.contains('is-open')) {
      setMobileNav(false);
    }
  });

  /* -----------------------------------------------------------------------
     Sticky Header
     ----------------------------------------------------------------------- */

  const header = document.querySelector('[data-header]');

  if (header && header.classList.contains('header--sticky')) {
    window.addEventListener('scroll', () => {
      if (window.scrollY > 100) {
        header.classList.add('header--scrolled');
      } else {
        header.classList.remove('header--scrolled');
      }
    }, { passive: true });
  }

  /* -----------------------------------------------------------------------
     Quantity Buttons (shared by PDP and Cart)
     ----------------------------------------------------------------------- */

  document.addEventListener('click', (e) => {
    const minus = e.target.closest('[data-quantity-minus]');
    const plus = e.target.closest('[data-quantity-plus]');

    if (minus) {
      const input = minus.parentElement.querySelector('[data-quantity-input]');
      if (input) {
        const val = parseInt(input.value, 10);
        if (val > parseInt(input.min || 1, 10)) {
          input.value = val - 1;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    }

    if (plus) {
      const input = plus.parentElement.querySelector('[data-quantity-input]');
      if (input) {
        input.value = parseInt(input.value, 10) + 1;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  });

  /* -----------------------------------------------------------------------
     Collection Sort
     ----------------------------------------------------------------------- */

  const sortSelect = document.querySelector('[data-sort-select]');

  if (sortSelect) {
    sortSelect.addEventListener('change', () => {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', sortSelect.value);
      window.location.href = url.toString();
    });
  }

  /* -----------------------------------------------------------------------
     Utility: Format Money
     ----------------------------------------------------------------------- */

  window.CRYIN = window.CRYIN || {};

  window.CRYIN.formatMoney = function (cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const value = (parseInt(cents, 10) / 100).toFixed(2);
    const fmt = format || window.CRYIN.moneyFormat || '€{{amount}}';
    return fmt
      .replace('{{amount}}', value)
      .replace('{{amount_no_decimals}}', Math.round(value))
      .replace('{{amount_with_comma_separator}}', value.replace('.', ','))
      .replace('{{amount_no_decimals_with_comma_separator}}', Math.round(value).toString().replace('.', ','));
  };

})();
